import styled from "@emotion/styled";
import { debounce } from "lodash";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Range } from "slate";
import { ReactEditor, useEditor, useSlate } from "slate-react";

import {
  ActionMenu,
  ActionMenuItem,
  Button,
  DesignToken,
  FormField,
  IActionMenuOption,
  Input,
  Loader,
  Modal,
  ModalPortal,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { ButtonGroup } from "components/button/ButtonGroup";
import { IconsEnum } from "components/icon";
import Flex from "components/layout/Flex";
import { ToolbarIcon } from "components/toolbar";
import useModal from "hooks/use-modal";
import { useAttachments } from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";
import { isUrl } from "utils/string";

interface IImagesIconProps {
  disabled: boolean;
  privateMode: boolean;
  mobile: boolean;
  tooltip?: string;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const buildUrl = (url: string) => {
  if (isUrl(url)) {
    return url;
  }

  if (isUrl(`https:${url}`)) {
    return `https:${url}`;
  }

  return null;
};

const ExternalImageModalFooter: React.FC<{ disabled: boolean; onClose: () => void }> = ({ disabled, onClose }) => (
  <ButtonGroup>
    <Button size="large" kind="secondary" onClick={onClose}>
      Cancel
    </Button>
    <Button size="large" kind="primary" type="submit" form="image-url-form" disabled={disabled}>
      Add
    </Button>
  </ButtonGroup>
);

const ExternalImageModal: React.FC<{ onClose: () => void; savedSelection: Range | null }> = ({
  onClose,
  savedSelection,
}) => {
  const image = useRef(new Image());
  const editor = useEditor();

  const [imageState, setImageState] = useState<"idle" | "loading" | "loaded" | "error">("idle");

  const [imageURL, setImageURL] = useState("");
  const debouncedSetPreview = useRef(
    debounce((url: string) => {
      const properUrl = buildUrl(url);

      if (properUrl) {
        setImageState("loading");
        image.current.src = url;
      } else if (url.startsWith("data") || /\s/.test(url)) {
        setImageState("error");
      }
    }, 250)
  );

  const handleURLChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setImageState("idle");
    setImageURL(event.target.value);
    debouncedSetPreview.current(event.target.value);
  }, []);

  useEffect(() => {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const onLoad = () => {
      setImageState("loaded");
    };

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const onError = () => {
      setImageState("error");
    };
    image.current.addEventListener("load", onLoad);
    image.current.addEventListener("error", onError);

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (image.current) {
        image.current.removeEventListener("load", onLoad);
        image.current.removeEventListener("error", onError);
      }
    };
  }, []);

  const handleAdd = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const url = image.current.src;
      const { naturalWidth: width, naturalHeight: height } = image.current;

      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor as any).insertExternalImage(url, { savedSelection, width, height });

      onClose();
    },
    [editor, onClose, savedSelection]
  );

  const stopFocusSteal = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <Modal
      onClose={onClose}
      onClick={stopFocusSteal}
      heading="Image from web address"
      footer={<ExternalImageModalFooter disabled={imageState !== "loaded"} onClose={onClose} />}
    >
      <form id="image-url-form" onSubmit={handleAdd}>
        <InputWrapper labelText="Image URL">
          <Input value={imageURL} onChange={handleURLChange} id="url" />
        </InputWrapper>
      </form>
      {imageState === "loading" && <Loader size="small" label="Loading preview…" />}
      {imageState === "loaded" && (
        <Flex column gap={withVar(SpacingToken.Spacing05)}>
          <Text size="sm">Preview</Text>
          <PreviewImageContainer>
            <img src={image.current.src} alt="Preview" />
          </PreviewImageContainer>
        </Flex>
      )}
      {imageState === "error" && (
        <Text customColor={withVar(DesignToken.ContentBasicNegative)}>
          Failed to load preview. Unsupported file type or wrong URL.
        </Text>
      )}
    </Modal>
  );
};

const ImagesIcon: React.FC<IImagesIconProps> = ({ mobile, disabled, privateMode, tooltip }) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [externalImageModalVisible, externalImageModal] = useModal(false);
  const [savedSelection, setSavedSelection] = useState<Range | null>(null);
  const editor = useSlate();

  const attachmentsProvider = useAttachments();

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      setMenuVisible(true);

      if (savedSelection !== null) {
        setSavedSelection(null);
      }

      if (ReactEditor.isFocused(editor as ReactEditor)) {
        // @ts-expect-error TS(2345)
        setSavedSelection({ ...editor.selection });
      }
    },
    [editor, savedSelection]
  );

  const handleUpload = useCallback(
    (files: File[]) => {
      const [file] = files;

      if (file) {
        const dataUrl = URL.createObjectURL(file);
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).insertLocalImage(dataUrl, file.name, { savedSelection });
      }
    },
    [editor, savedSelection]
  );

  const handleMenuClose = useCallback(() => {
    setMenuVisible(false);
  }, []);

  const options: IActionMenuOption[] = [
    {
      key: "image-from-url",
      element: <ActionMenuItem>Image from URL</ActionMenuItem>,
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      onClick: () => {
        externalImageModal.show(true);
        handleMenuClose();
      },
    },
    {
      key: "choose-image",
      element: <ActionMenuItem>Choose image</ActionMenuItem>,
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      onClick: () => {
        attachmentsProvider.openFileDialog({
          accept: "image/jpeg,image/png,image/gif,image/webp,image/bmp",
          multiple: false,
          onFileSelect: handleUpload,
        });
        handleMenuClose();
      },
    },
  ];

  return (
    <>
      <ModalPortal zIndex={StackingContextLevel.Modal}>
        {externalImageModalVisible && (
          <ExternalImageModal onClose={externalImageModal.close} savedSelection={savedSelection} />
        )}
      </ModalPortal>
      <ActionMenu
        options={options}
        visible={menuVisible}
        onClose={handleMenuClose}
        placement="top-start"
        triggerRenderer={
          <span>
            <ToolbarIcon
              icon={IconsEnum.Image}
              mobile={mobile}
              disabled={disabled}
              privateMode={privateMode}
              tooltip={!menuVisible ? tooltip : undefined}
              testSelector="images-icon"
              onMouseDown={handleMenuOpen}
            />
          </span>
        }
      />
    </>
  );
};

const InputWrapper = styled(FormField)`
  width: 320px;
  min-width: 100%;
  max-width: 100%;
`;

const PreviewImageContainer = styled.div`
  img {
    max-width: 500px;
    height: auto;
  }

  @media (max-width: 500px) {
    img {
      max-width: 300px;
      height: auto;
    }
  }
`;

export default ImagesIcon;
